import { useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';

/**
 * Hook personalizado para verificar permisos de empresa
 * 
 * @returns {Object} Objeto con funciones de verificación de permisos
 */
export const useEmpresaPermisos = () => {
  const { empresaId } = useContext(AuthContext);

  /**
   * Verifica si la empresa actual es la empresa 9 (tiene acceso completo)
   * @returns {boolean} true si es empresa 9, false en caso contrario
   */
  const esEmpresaConAccesoCompleto = () => {
    return empresaId === '9' || empresaId === 9;
  };

  /**
   * Verifica si un reporte específico está disponible para la empresa actual
   * @param {string} reporteNombre - Nombre del reporte a verificar
   * @returns {boolean} true si el reporte está disponible, false en caso contrario
   */
  const tieneAccesoAReporte = (reporteNombre) => {
    // Si es empresa 9, tiene acceso a todos los reportes
    if (esEmpresaConAccesoCompleto()) {
      return true;
    }

    // Solo "Apagados / Encendidos" es exclusivo de la empresa 9
    const reportesExclusivosEmpresa9 = [
      'Apagados / Encendidos'
    ];

    // Si el reporte es exclusivo de empresa 9 y no es empresa 9, denegar acceso
    return !reportesExclusivosEmpresa9.includes(reporteNombre);
  };

  /**
   * Obtiene la lista de reportes disponibles para la empresa actual
   * @returns {Array} Array con los nombres de reportes disponibles
   */
  const getReportesDisponibles = () => {
    if (esEmpresaConAccesoCompleto()) {
      // Empresa 9 tiene acceso a todos los reportes
      return [
        'Alertas',
        'Recorridos', 
        'Apagados / Encendidos', // Solo para empresa 9
        'Estadías',
        'Usabilidad',
        'Kilómetros',
        'Paradas No Autorizadas',
        'Radar',
        'Consumos',
        'Bitácora'
      ];
    } else {
      // Otras empresas tienen acceso a todos excepto "Apagados / Encendidos"
      return [
        'Alertas',
        'Recorridos',
        'Estadías',
        'Usabilidad',
        'Kilómetros',
        'Paradas No Autorizadas',
        'Radar',
        'Consumos',
        'Bitácora'
      ];
    }
  };

  /**
   * Obtiene información de debug sobre los permisos actuales
   * @returns {Object} Información de debug
   */
  const getDebugInfo = () => {
    return {
      empresaId,
      esEmpresaConAccesoCompleto: esEmpresaConAccesoCompleto(),
      reportesDisponibles: getReportesDisponibles(),
      timestamp: new Date().toISOString()
    };
  };

  /**
   * Filtra rutas de navegación basándose en permisos de empresa
   * @param {Array} routes - Array de rutas a filtrar
   * @returns {Array} Array de rutas filtradas según permisos
   */
  const filtrarRutasPorPermisos = (routes) => {
    if (!routes || !Array.isArray(routes)) {
      return [];
    }

    return routes.filter(route => {
      // Si es un separador, mantenerlo
      if (route.type === 'divider') {
        return true;
      }

      // Si la ruta tiene hijos, filtrar recursivamente
      if (route.children && Array.isArray(route.children)) {
        const hijosPermitidos = filtrarRutasPorPermisos(route.children);
        
        // Si no tiene hijos permitidos después del filtrado, no mostrar el padre
        if (hijosPermitidos.length === 0) {
          return false;
        }
        
        // Crear una copia de la ruta con los hijos filtrados
        Object.assign(route, { children: hijosPermitidos });
        return true;
      }

      // Para rutas sin hijos, verificar si tiene acceso
      return tieneAccesoAReporte(route.name);
    });
  };

  return {
    empresaId,
    esEmpresaConAccesoCompleto,
    tieneAccesoAReporte,
    getReportesDisponibles,
    getDebugInfo,
    filtrarRutasPorPermisos
  };
};

export default useEmpresaPermisos;
